/* eslint-disable camelcase */
import { Column, ColumnSortEvent } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useTranslation } from 'react-i18next';

import { Locale } from '../../i18n';
import EmissionValueWithSuffix from '../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { parseDateWithoutTime } from '../../shared/helpers/parse-date';
import { EmissionSymbol, IFootPrintTableRow } from '../../shared/interfaces/IFootprint';
import FootprintStatus from './footprint-status/FootprintStatus';
import style from './PcfTable.module.css';

export type PcfTableProps = {
  footprints: IFootPrintTableRow[];
  isLoading?: boolean;
  onRowClick?: (rowData: IFootPrintTableRow) => void;
  actions?: (rowData: IFootPrintTableRow) => JSX.Element | null;
};

const PcfTable = ({ footprints, isLoading, onRowClick, actions }: PcfTableProps): JSX.Element => {
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const pcfIdTemplate = (rowData: IFootPrintTableRow): JSX.Element | string => {
    const onPCFIdClick = () => {
      if (onRowClick) {
        onRowClick(rowData);
      }
    };

    return (
      <div className='flex flex-column justify-content-center'>
        <div
          onClick={onPCFIdClick}
          className='font-normal max-w-fit hover:underline cursor-pointer'
        >
          {rowData.footprintId}
        </div>
      </div>
    );
  };

  const pcfTemplate = (rowData: IFootPrintTableRow): JSX.Element | string => {
    const emissionValue = rowData?.pcfExcludingBiogenic || 0;

    if (!emissionValue) {
      return t('global.notSpecified');
    }

    return (
      <EmissionValueWithSuffix
        emissionValue={emissionValue}
        emissionSymbol={EmissionSymbol.KG_CO2E}
        declaredUnit={rowData.declaredUnit}
        abbreviateUnit
        unitaryProductAmount={rowData.unitaryProductAmount}
      />
    );
  };

  const dateCreatedTemplate = (rowData: IFootPrintTableRow): React.ReactNode => {
    return parseDateWithoutTime(rowData.dateCreated ?? '', language as Locale);
  };

  const footprintStatusTemplate = (rowData: IFootPrintTableRow) => {
    return <FootprintStatus status={rowData.status} />;
  };

  const sortPcfValueColumn = (event: ColumnSortEvent) => {
    const data = [...event.data] as IFootPrintTableRow[];
    const eventOrder = event.order || 1;

    data.sort((data1, data2) => {
      const pcf1 = data1?.pcfExcludingBiogenic || 0;
      const pcf2 = data2?.pcfExcludingBiogenic || 0;

      let result = 1;

      if (pcf1 < pcf2) {
        result = -1;
      } else if (pcf1 > pcf2) {
        result = 1;
      } else {
        result = 0;
      }

      return eventOrder * result;
    });

    return data;
  };

  const pcfColumnHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex flex-column'>
        <span>{t('productDetailsPage.tabs.pcfTab.pcfTableColumns.pcfValue')}</span>
        <span className='text-sm font-light'>
          {t('productDetailsPage.tabs.pcfTab.pcfTableColumns.exclBiogenicEmissions')}
        </span>
      </div>
    );
  };

  return (
    <div className={style['pcf-table']}>
      <DataTable
        value={footprints}
        loading={isLoading}
        selectionMode='single'
        sortField='dateCreated'
        sortOrder={-1}
        scrollable
        scrollHeight='600px'
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          header={t('productDetailsPage.tabs.pcfTab.pcfTableColumns.pcfId')}
          body={pcfIdTemplate}
          className='w-3'
        />
        <Column
          body={dateCreatedTemplate}
          header={t('productDetailsPage.tabs.pcfTab.pcfTableColumns.dateCreated')}
          sortable
          sortField='dateCreated'
          className='w-3'
        />
        <Column
          header={t('productDetailsPage.tabs.pcfTab.pcfTableColumns.status')}
          body={footprintStatusTemplate}
          sortable
          sortField='status'
          className='w-2'
        />
        <Column
          header={pcfColumnHeaderTemplate}
          body={pcfTemplate}
          sortable
          sortField='pcfExcludingBiogenic'
          sortFunction={sortPcfValueColumn}
          className={actions ? 'w-2' : 'w-4'}
        />
        {actions && <Column body={actions} className='w-2' />}
      </DataTable>
    </div>
  );
};

export default PcfTable;
